/**
 * Athos-powered autocomplete for the theme's <form is="predictive-search"> (see assets/search.js).
 *
 * Flow (https://docs.athoscommerce.com/reference/autocomplete-module):
 *   1. Suggest API   -> query suggestions for what the shopper has typed
 *   2. Autocomplete API -> product preview for the best suggestion (never the Search API)
 *   3. Hovering/focusing another suggestion re-runs step 2 for that suggestion
 *
 * Autocomplete requests carry q (the suggestion used), input (what was typed) and
 * source (input | suggested) so Athos reporting can tell typed from suggested queries.
 */
(function () {
  if (!window.Athos) return;

  const MIN_CHARS = 2;
  const PRODUCT_LIMIT_FALLBACK = 4;
  const CONTENT_LIMIT = 3;
  // dropdown heading for each extra Athos account (strings come from predictive-search.liquid)
  const CONTENT_HEADINGS = { collections: 'collections', blog: 'articles' };

  const escapeHtml = (value) =>
    String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  // Athos returns some text already HTML-escaped (e.g. "Zen &amp; Art"); decode before re-escaping.
  const decodeEntities = (value) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value === null || value === undefined ? '' : value;
    return textarea.value;
  };
  const text = (value) => escapeHtml(decodeEntities(value));

  const formatPrice = (price) => {
    const amount = parseFloat(price);
    if (Number.isNaN(amount)) return '';
    const format = window.FoxTheme && FoxTheme.settings && FoxTheme.settings.moneyFormat;
    if (format && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      return FoxTheme.Currency.formatMoney(Math.round(amount * 100), format);
    }
    return amount.toFixed(2);
  };

  class AthosAutocomplete {
    constructor(form) {
      this.form = form;
      this.strings = JSON.parse(form.dataset.athosStrings || '{}');
      this.limit = parseInt(form.dataset.resultsLimit, 10) || PRODUCT_LIMIT_FALLBACK;
      this.topLayout = form.dataset.drawerPosition === 'top';
      this.requestId = 0;
      this.controller = null;
      this.typed = '';
      this.responseId = null;
      this.shown = [];
      this.contentShown = {};

      // delegated so it survives re-rendering of the results
      form.addEventListener('mouseover', (event) => this.onSuggestionIntent(event));
      form.addEventListener('focusin', (event) => this.onSuggestionIntent(event));
      form.addEventListener('click', (event) => this.onProductClick(event));
      form.addEventListener('click', (event) => this.onContentClick(event));
    }

    get target() {
      return document.getElementById('PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this.form));
    }

    /** Called by search.js whenever the input changes or regains focus. Returns false if nothing to do. */
    async search(query) {
      this.typed = query;
      if (query.length < MIN_CHARS) return false;

      const requestId = ++this.requestId;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      // Suggestions are a nice-to-have: if the Suggest API times out (503) still show products.
      const suggest = await window.Athos.request('suggest', { q: query }, { signal }).catch((error) => {
        if (error.name === 'AbortError') throw error;
        console.warn('Athos suggest failed, continuing without suggestions', error);
        return {};
      });
      if (requestId !== this.requestId) return true;

      const suggestions = this.collectSuggestions(suggest, query);
      const best = suggestions[0] || query;
      const [autocomplete, content] = await Promise.all([this.fetchProducts(best, query, signal), this.fetchContent(best, query, signal)]);
      if (requestId !== this.requestId) return true;

      this.render(suggestions, autocomplete.results || [], best, query, content, autocomplete);
      this.trackShown(autocomplete);
      this.trackContent(content);
      return true;
    }

    collectSuggestions(suggest, query) {
      const terms = [];
      const add = (entry) => {
        const term = entry && entry.text && decodeEntities(entry.text).trim();
        if (term && !terms.includes(term)) terms.push(term);
      };
      add(suggest.suggested);
      (suggest.alternatives || []).forEach(add);
      // only offer suggestions that differ from nothing, but never an empty list entry
      return terms.filter((term) => term.length > 0).slice(0, 4);
    }

    /**
     * Collections and blog articles each live in their own Athos account. They are optional,
     * so a failure just hides that group. Returns [{ key, siteId, responseId, results }] for groups with results.
     */
    async fetchContent(q, input, signal) {
      const sources = window.Athos.contentSources || [];
      const source = q.toLowerCase() === input.toLowerCase() ? 'input' : 'suggested';
      const groups = await Promise.all(
        sources.map(async (entry) => {
          try {
            const data = await window.Athos.request(
              'autocomplete',
              { q, input, source, resultsPerPage: CONTENT_LIMIT },
              { signal, siteId: entry.siteId }
            );
            return { key: entry.key, siteId: entry.siteId, responseId: data.responseId, results: data.results || [] };
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            console.warn(`Athos: ${entry.key} suggestions unavailable`, error);
            return null;
          }
        })
      );
      return groups.filter((group) => group && group.results.length);
    }

    fetchProducts(q, input, signal) {
      return window.Athos.request(
        'autocomplete',
        {
          q,
          input,
          source: q.toLowerCase() === input.toLowerCase() ? 'input' : 'suggested',
          resultsPerPage: this.limit
        },
        { signal }
      );
    }

    /** The dropdown shows its products immediately, so each response is one impression. */
    trackShown(data) {
      this.responseId = data.responseId;
      this.shown = data.results || [];
      this.bannerUids = this.bannerUidsFrom(data);
      if (window.AthosTracking) {
        window.AthosTracking.render('autocomplete', this.responseId);
        window.AthosTracking.impression('autocomplete', this.responseId, this.shown, this.bannerUids.map((uid) => ({ uid })));
      }
    }

    trackContent(groups) {
      this.contentShown = {};
      const tracking = window.AthosTracking && window.AthosTracking.forSite;
      groups.forEach((group) => {
        this.contentShown[group.key] = group;
        if (tracking) tracking(group.siteId).render('autocomplete', group.responseId);
        if (tracking) tracking(group.siteId).impression('autocomplete', group.responseId, group.results);
      });
    }

    onContentClick(event) {
      if (!window.AthosTracking || !window.AthosTracking.forSite || !event.target.closest('a[href]')) return;
      const list = event.target.closest('[data-athos-content-key]');
      const item = event.target.closest('[data-athos-index]');
      const group = list && this.contentShown[list.dataset.athosContentKey];
      if (!group || !item) return;
      window.AthosTracking.forSite(group.siteId).clickThrough('autocomplete', group.responseId, group.results[Number(item.dataset.athosIndex)]);
    }

    onProductClick(event) {
      if (!window.AthosTracking) return;
      const banner = event.target.closest('[data-athos-dd-banner]');
      if (banner && event.target.closest('a[href]') && banner.dataset.bannerUid) {
        window.AthosTracking.clickThrough('autocomplete', this.responseId, null, banner.dataset.bannerUid);
        return;
      }
      if (!event.target.closest('[data-athos-products]')) return;
      const item = event.target.closest('[data-athos-index]');
      if (!item || !event.target.closest('a[href]')) return;
      window.AthosTracking.clickThrough('autocomplete', this.responseId, this.shown[Number(item.dataset.athosIndex)]);
    }

    onSuggestionIntent(event) {
      const item = event.target.closest && event.target.closest('[data-athos-suggestion]');
      if (!item || item.dataset.active === 'true') return;
      this.previewSuggestion(item.dataset.athosSuggestion);
    }

    async previewSuggestion(term) {
      const requestId = ++this.requestId;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const signal = this.controller.signal;
        const [data, content] = await Promise.all([this.fetchProducts(term, this.typed, signal), this.fetchContent(term, this.typed, signal)]);
        if (requestId !== this.requestId) return;
        const list = this.target && this.target.querySelector('[data-athos-products]');
        if (list) list.innerHTML = this.productsMarkup(data.results || []);
        this.trackShown(data);
        const bannersTop = this.target && this.target.querySelector('[data-athos-banners-top]');
        if (bannersTop) bannersTop.innerHTML = this.bannerMarkup(data, ['header', 'banner'], 'top');
        const bannersBottom = this.target && this.target.querySelector('[data-athos-banners-bottom]');
        if (bannersBottom) bannersBottom.innerHTML = this.bannerMarkup(data, ['footer'], 'bottom');
        const contentWrap = this.target && this.target.querySelector('[data-athos-content-groups]');
        if (contentWrap) contentWrap.innerHTML = this.contentGroupsMarkup(content);
        this.trackContent(content);
        this.target.querySelectorAll('[data-athos-suggestion]').forEach((el) => {
          el.dataset.active = el.dataset.athosSuggestion === term ? 'true' : 'false';
        });
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      }
    }

    /** Merchandising banners come back as ready-made HTML keyed by position (header, banner, footer, left, ...). */
    bannerHtml(data, positions) {
      const slots = (data && data.merchandising && data.merchandising.content) || {};
      return positions.map((position) => [].concat(slots[position] || []).join('')).join('');
    }

    bannerUidsFrom(data) {
      const html = this.bannerHtml(data, ['header', 'banner', 'footer']);
      return [...html.matchAll(/data-banner-id="(\d+)"/g)].map((match) => match[1]);
    }

    bannerMarkup(data, positions, slot) {
      const html = this.bannerHtml(data, positions);
      if (!html.trim()) return '';
      const uid = (html.match(/data-banner-id="(\d+)"/) || [])[1] || '';
      return `<div class="athos-search__banner" data-athos-dd-banner="${slot}" data-banner-uid="${uid}">${html}</div>`;
    }

    /** Blog articles get a thumbnail, title and date (like product rows); other groups stay as text links. */
    articleItemMarkup(result, index) {
      const image = window.Athos.articleImage(result);
      const date = result.published_at ? new Date(result.published_at) : null;
      const dateText = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(undefined, { dateStyle: 'medium' }) : '';
      const url = escapeHtml(result.url);
      return `
        <li class="predictive-search__list-item" role="option" aria-selected="false" data-athos-index="${index}">
          <div class="flex items-center gap-3">
            ${
              image
                ? `<a href="${url}" class="media-wrapper blocks-radius-sm" tabindex="-1" aria-hidden="true">${this.imageMarkup({ thumbnailImageUrl: image })}</a>`
                : '<span class="media-wrapper blocks-radius-sm athos-search__thumb-placeholder" aria-hidden="true"></span>'
            }
            <div class="grid gap-1">
              <p class="block m-0"><a href="${url}" class="reversed-link text-pcard-title">${text(result.name)}</a></p>
              ${dateText ? `<span class="text-sm text-subtext">${escapeHtml(dateText)}</span>` : ''}
            </div>
          </div>
        </li>`;
    }

    contentGroupsMarkup(groups) {
      const top = this.topLayout;
      return groups
        .map((group) => {
          const rich = group.key === 'blog';
          const items = group.results
            .map((result, index) =>
              rich
                ? this.articleItemMarkup(result, index)
                : `
              <li class="predictive-search-result__list-item" role="option" aria-selected="false" data-athos-index="${index}">
                <a href="${escapeHtml(result.url)}" class="reversed-link">${text(result.name)}</a>
              </li>`
            )
            .join('');
          const listClass = rich
            ? `grid gap-4${top ? ' md:grid-cols-3' : ''}`
            : `predictive-search-result__list flex flex-wrap gap-x-5 gap-y-2${top ? ' md:justify-center' : ''}`;
          const viewAll =
            group.key === 'blog'
              ? `<a href="${escapeHtml(this.searchUrl(this.typed, { type: 'article' }))}" class="reversed-link text-sm font-body-bolder${top ? ' md:justify-self-center' : ''}">${escapeHtml(this.strings.viewAllArticles)}</a>`
              : '';
          return `
          <div class="grid predictive-search-result search__box-item predictive-search-result--article${top ? ' gap-4 md:text-center' : ' gap-3'}">
            <h4 class="predictive-search-result__heading h5${top ? ' md:text-center' : ''}">${escapeHtml(this.strings[CONTENT_HEADINGS[group.key]])}</h4>
            <ul role="group" class="${listClass}" data-athos-content-key="${escapeHtml(group.key)}">${items}</ul>
            ${viewAll}
          </div>`;
        })
        .join('');
    }

    render(suggestions, results, best, query, content, data) {
      const target = this.target;
      if (!target) return;
      const s = this.strings;
      const top = this.topLayout;

      if (!results.length && !suggestions.length && !(content || []).length) {
        target.innerHTML = `
          <div class="empty-state flex flex-col gap-3 items-center justify-center text-center w-full h-full">
            <h4>${escapeHtml((s.noResults || '').replace('__TERMS__', query))}</h4>
            <p class="m-0">${escapeHtml(s.noResultsMessage)}</p>
          </div>`;
        return;
      }

      const suggestionItems = suggestions
        .map(
          (term, i) => `
          <li class="predictive-search-result__list-item" role="option" aria-selected="false">
            <a href="${escapeHtml(this.searchUrl(term))}" class="reversed-link" data-athos-suggestion="${escapeHtml(term)}" data-active="${term === best ? 'true' : 'false'}">${this.highlight(term, query)}</a>
          </li>`
        )
        .join('');

      // The suggestions still choose which products are previewed; the setting only shows or hides the list.
      const showSuggestions = this.form.dataset.showSuggestions === 'true';
      const suggestionBlock = showSuggestions && suggestions.length
        ? `<div class="grid predictive-search-result search__box-item${top ? ' predictive-search-result--queries-top gap-4' : ' gap-3'}">
            <h4 class="predictive-search-result__heading h5${top ? ' md:text-center' : ''}">${escapeHtml(s.suggestions)}</h4>
            <ul role="group" class="predictive-search-result__list flex flex-wrap gap-x-5 gap-y-2${top ? ' md:justify-center' : ''}">${suggestionItems}</ul>
          </div>`
        : '';

      target.innerHTML = `
        <div class="grid gap-6 w-full">
          <div class="contents" data-athos-banners-top>${this.bannerMarkup(data, ['header', 'banner'], 'top')}</div>
          ${suggestionBlock}
          <div class="grid predictive-search-result search__box-item predictive-search-result--products${top ? ' gap-4' : ' gap-3'}">
            <h4 class="predictive-search-result__heading h5${top ? ' md:text-center' : ''}">${escapeHtml(s.products)}</h4>
            <div data-athos-products>${this.productsMarkup(results)}</div>
            ${
              results.length
                ? `<a href="${escapeHtml(this.searchUrl(query))}" class="reversed-link text-sm font-body-bolder${top ? ' md:justify-self-center' : ''}">${escapeHtml(s.viewAllProducts)}</a>`
                : ''
            }
          </div>
          <div class="contents" data-athos-content-groups>${this.contentGroupsMarkup(content || [])}</div>
          <div class="contents" data-athos-banners-bottom>${this.bannerMarkup(data, ['footer'], 'bottom')}</div>
        </div>
        <div class="search__results-all${top ? ' flex md:justify-center' : ''}">
          <button type="submit" class="reversed-link btn--inherit flex items-center justify-center gap-2 font-body-bolder">
            ${escapeHtml((s.searchFor || '').replace('__TERMS__', query))}
            ${s.arrowIcon || ''}
          </button>
        </div>`;

      const searchContent = this.form.querySelector('.search__content');
      if (searchContent) searchContent.classList.remove('hidden');
    }

    productsMarkup(results) {
      const showPrice = this.form.dataset.showPrice === 'true';
      if (!results.length) return '';

      if (this.topLayout) {
        return `<div class="f-grid f-grid-2-cols md:f-grid-4-cols f-grid--gap-extra-small" role="group">${results
          .map(
            (result, index) => `
            <div class="f-column predictive-search__list-item" role="option" aria-selected="false" data-athos-index="${index}">
              <a href="${escapeHtml(result.url)}" class="grid gap-2">
                ${this.imageMarkup(result)}
                <span class="reversed-link text-pcard-title">${text(result.name)}</span>
                ${showPrice ? `<span>${escapeHtml(formatPrice(result.price))}</span>` : ''}
              </a>
            </div>`
          )
          .join('')}</div>`;
      }

      return `<ul class="grid gap-4" role="group">${results
        .map(
          (result, index) => `
          <li class="predictive-search__list-item" role="option" aria-selected="false" data-athos-index="${index}">
            <div class="flex items-center gap-3">
              <a href="${escapeHtml(result.url)}" class="media-wrapper blocks-radius-sm" tabindex="-1" aria-hidden="true">${this.imageMarkup(result)}</a>
              <div class="grid gap-1">
                <p class="block m-0"><a href="${escapeHtml(result.url)}" class="reversed-link text-pcard-title">${text(result.name)}</a></p>
                ${showPrice ? `<span>${escapeHtml(formatPrice(result.price))}</span>` : ''}
              </div>
            </div>
          </li>`
        )
        .join('')}</ul>`;
    }

    imageMarkup(result) {
      const src = result.thumbnailImageUrl || result.imageUrl;
      if (!src) return '';
      return `<img src="${escapeHtml(decodeEntities(src))}" alt="" width="100" loading="lazy" is="image-lazy">`;
    }

    // The theme editor adds ?oseid=... to the form action, so build the URL properly instead of
    // appending "?q=" (which produced /search?oseid=...?q=... and broke the editor preview).
    searchUrl(term, extra) {
      const url = new URL(this.form.getAttribute('action') || '/search', window.location.origin);
      url.searchParams.set('q', term);
      Object.keys(extra || {}).forEach((key) => url.searchParams.set(key, extra[key]));
      return url.pathname + url.search;
    }

    // bold the part the shopper has not typed yet
    highlight(term, query) {
      if (term.toLowerCase().startsWith(query.toLowerCase())) {
        return `${escapeHtml(term.slice(0, query.length))}<strong>${escapeHtml(term.slice(query.length))}</strong>`;
      }
      return escapeHtml(term);
    }
  }

  window.AthosAutocomplete = {
    for(form) {
      if (!form._athosAutocomplete) form._athosAutocomplete = new AthosAutocomplete(form);
      return form._athosAutocomplete;
    }
  };
})();
